import { area, booleanContains, polygon } from '@turf/turf'
import type { CoverageZone, ReportTypeRulesBase } from '@xweather/protect-types'
import type { Duration } from 'dayjs/plugin/duration'
import { findKey } from 'es-toolkit'
import type { MultiPolygon, Polygon } from 'geojson'
import { dayjs } from '../../../dayjs.ts'

export const areaKm2 = (geometry: Polygon | MultiPolygon) => area(geometry) / 1_000_000

export const maxAreaKm2 = ({ maxDurationByArea }: ReportTypeRulesBase) =>
	Math.max(0, ...maxDurationByArea.map(({ upToAreaKm2 }) => upToAreaKm2))

export const maxRadiusKmForArea = (areaKm2: number) => Math.floor(Math.sqrt(areaKm2 / Math.PI))

export const exceedsMaxArea = (rules: ReportTypeRulesBase, geometry: MultiPolygon) => areaKm2(geometry) > maxAreaKm2(rules)

export const maxDurationForArea = ({ maxDurationByArea }: ReportTypeRulesBase, geometry: MultiPolygon) => {
	const km2 = areaKm2(geometry)
	const limit = maxDurationByArea.find(({ upToAreaKm2 }) => km2 <= upToAreaKm2) ?? maxDurationByArea.at(-1)
	return limit && dayjs.duration(limit.maxDuration)
}

export const isShorterThanMinDuration = ({ minDuration }: ReportTypeRulesBase, duration: Duration) =>
	duration.asMilliseconds() < dayjs.duration(minDuration).asMilliseconds()

export const exceedsMaxDuration = ({ durationTolerance = 'PT0S' }: ReportTypeRulesBase, maxDuration: Duration, duration: Duration) =>
	duration.asMilliseconds() > maxDuration.add(dayjs.duration(durationTolerance)).asMilliseconds()

const polygonsOf = (geometry: Polygon | MultiPolygon): Polygon[] =>
	geometry.type === 'Polygon' ? [geometry] : geometry.coordinates.map((coordinates) => polygon(coordinates).geometry)

// booleanContains only takes a Polygon inside a Polygon.
export const isInsideCoverageZone = ({ geometry: zone }: CoverageZone, geometry: Polygon | MultiPolygon) =>
	zone === null || polygonsOf(geometry).every((part) => polygonsOf(zone).some((zonePart) => booleanContains(zonePart, part)))

export const coverageZoneOf = <TZone extends string>(coverageZones: Record<TZone, CoverageZone>, geometry: MultiPolygon) =>
	findKey(coverageZones, (zone) => isInsideCoverageZone(zone, geometry))
